import { supabase } from './supabase';

export interface GHLSyncResult {
    success: boolean;
    message: string;
    error?: string;
    ghlContactId?: string;
}

export class GHLSync {

    /**
     * Sync a contact to GoHighLevel after creation or update
     */
    static async syncContactToGHL(contactId: string, isUpdate: boolean = false): Promise<GHLSyncResult> {
        try {
            const { data, error } = await supabase.functions.invoke('ghl-sync', {
                body: {
                    action: isUpdate ? 'update' : 'create',
                    contactId: contactId
                }
            });

            if (error) {
                console.error('GHL Sync Error:', error);
                return {
                    success: false,
                    message: 'Failed to sync with GoHighLevel',
                    error: error.message
                };
            }

            return data;
        } catch (error) {
            console.error('GHL Sync Exception:', error);
            return {
                success: false,
                message: 'Failed to sync with GoHighLevel',
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    /**
     * Delete a contact from GoHighLevel
     */
    static async deleteContactFromGHL(ghlContactId: string): Promise<GHLSyncResult> {
        try {
            const { data, error } = await supabase.functions.invoke('ghl-sync', {
                body: {
                    action: 'delete',
                    ghlContactId: ghlContactId
                }
            });

            if (error) {
                console.error('GHL Delete Error:', error);
                return {
                    success: false,
                    message: 'Failed to delete from GoHighLevel',
                    error: error.message
                };
            }

            return data;
        } catch (error) {
            console.error('GHL Delete Exception:', error);
            return {
                success: false,
                message: 'Failed to delete from GoHighLevel',
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    /**
     * Sync all contacts to GoHighLevel (bulk operation)
     */
    static async syncAllContactsToGHL(): Promise<GHLSyncResult> {
        try {
            const { data, error } = await supabase.functions.invoke('ghl-sync', {
                body: {
                    action: 'sync_all'
                }
            });

            if (error) {
                console.error('GHL Bulk Sync Error:', error);
                return {
                    success: false,
                    message: 'Failed to bulk sync with GoHighLevel',
                    error: error.message
                };
            }

            return data;
        } catch (error) {
            console.error('GHL Bulk Sync Exception:', error);
            return {
                success: false,
                message: 'Failed to bulk sync with GoHighLevel',
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    /**
     * QUICK TEST: Pull 5 contacts with ALL 91 custom fields (for testing)
     */
    static async syncGHLContactsToSupabase(): Promise<GHLSyncResult> {
        try {
            console.log('🧪 Quick Test: Pulling 5 contacts with ALL custom fields...');

            const { data, error } = await supabase.functions.invoke('ghl-sync', {
                body: {
                    full_data_sync: true,  // ← Use full data mode for complete fields
                    test_mode: true,       // ← Limit to 5 contacts for testing
                    limit: 5
                }
            });

            if (error) {
                console.error('GHL Quick Test Error:', error);
                return {
                    success: false,
                    message: 'Failed to test sync GoHighLevel contacts',
                    error: error.message
                };
            }

            console.log('✅ Quick Test Complete:', data);

            // Log debug info to see what fields GHL is actually sending
            if (data.debugInfo) {
                console.log('🔍 DEBUG INFO - Raw GHL Contact Sample:', data.debugInfo.sampleContact?.rawGHL);
                console.log('🗂️ DEBUG INFO - Key Field Mapping:', data.debugInfo.sampleContact?.mappedFields);
            }

            // Log detailed sync results to see the specific errors
            if (data.syncResults) {
                console.log('📋 Detailed Sync Results:', data.syncResults);
                data.syncResults.forEach((result: any, index: number) => {
                    if (result.status === 'error') {
                        console.error(`❌ Contact ${index + 1} (${result.id}) Error:`, result.error);
                    } else {
                        console.log(`✅ Contact ${index + 1} (${result.id}):`, result.status);
                    }
                });
            }

            return {
                success: true,
                message: data.message || 'Quick test completed - 5 contacts with full data',
                ...data
            };
        } catch (error) {
            console.error('GHL Quick Test Exception:', error);
            return {
                success: false,
                message: 'Failed to test sync GoHighLevel contacts',
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    /**
     * FULL DATA SYNC: Pulls ALL 1,500 contacts with ALL 91 custom fields (production)
     */
    static async fullDataSyncFromGHL(): Promise<GHLSyncResult> {
        try {
            console.log('🔥 Starting FULL DATA SYNC - This will take 15-20 minutes for ALL contacts with complete data...');

            const { data, error } = await supabase.functions.invoke('ghl-sync', {
                body: {
                    full_data_sync: true  // ← Full production sync - no limits
                }
            });

            if (error) {
                console.error('GHL Full Data Sync Error:', error);
                return {
                    success: false,
                    message: 'Failed to perform full data sync from GoHighLevel',
                    error: error.message
                };
            }

            console.log('🎉 FULL DATA SYNC COMPLETE:', data);

            return {
                success: true,
                message: data.message || 'Full data sync completed successfully',
                ...data
            };
        } catch (error) {
            console.error('GHL Full Data Sync Exception:', error);
            return {
                success: false,
                message: 'Failed to perform full data sync from GoHighLevel',
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    /**
     * Test connection to GoHighLevel via Supabase Edge Function
     */
    static async testGHLConnection(): Promise<GHLSyncResult> {
        try {
            console.log('🚀 Testing GHL connection via Supabase Edge Function...');

            const { data, error } = await supabase.functions.invoke('ghl-sync', {
                // No body - the Edge Function ignores it anyway
            });

            if (error) {
                console.error('GHL Connection Test Error:', error);
                return {
                    success: false,
                    message: 'Failed to connect to GoHighLevel via Supabase',
                    error: error.message
                };
            }

            console.log('✅ GHL Connection Test Response:', data);

            return {
                success: true,
                message: `Connected to GoHighLevel! Retrieved ${data.data?.contacts?.length || 0} contacts`,
                ...data
            };
        } catch (error) {
            console.error('GHL Connection Test Exception:', error);
            return {
                success: false,
                message: 'Failed to connect to GoHighLevel via Supabase',
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    /**
     * Pull contacts from GoHighLevel to Supabase
     */
    static async pullContactsFromGHL(): Promise<GHLSyncResult> {
        try {
            const { data, error } = await supabase.functions.invoke('ghl-sync', {
                body: {
                    action: 'pull_from_ghl'
                }
            });

            if (error) {
                console.error('GHL Pull Error:', error);
                return {
                    success: false,
                    message: 'Failed to pull contacts from GoHighLevel',
                    error: error.message
                };
            }

            return data;
        } catch (error) {
            console.error('GHL Pull Exception:', error);
            return {
                success: false,
                message: 'Failed to pull contacts from GoHighLevel',
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    /**
     * Get sync status for a contact
     */
    static getSyncStatus(contact: any): 'synced' | 'pending' | 'never' | 'error' {
        if (!contact.ghl_contact_id) {
            return 'never';
        }

        if (!contact.ghl_last_synced) {
            return 'pending';
        }

        const lastSynced = new Date(contact.ghl_last_synced);
        const lastUpdated = new Date(contact.updated_at || contact.created_at);

        if (lastSynced < lastUpdated) {
            return 'pending';
        }

        return 'synced';
    }

    /**
     * Format sync status for display
     */
    static formatSyncStatus(contact: any): { status: string; className: string; icon: string } {
        const syncStatus = this.getSyncStatus(contact);

        switch (syncStatus) {
            case 'synced':
                return {
                    status: 'Synced with GHL',
                    className: 'text-green-600',
                    icon: '✓'
                };
            case 'pending':
                return {
                    status: 'Sync pending',
                    className: 'text-yellow-600',
                    icon: '⏳'
                };
            case 'never':
                return {
                    status: 'Not synced',
                    className: 'text-gray-500',
                    icon: '○'
                };
            case 'error':
                return {
                    status: 'Sync error',
                    className: 'text-red-600',
                    icon: '⚠'
                };
            default:
                return {
                    status: 'Unknown',
                    className: 'text-gray-500',
                    icon: '?'
                };
        }
    }
}
